/*
 * Copyright (c) 2020 Via Transportation, Inc. All rights reserved.
 *
 * This software is distributed under license and may not be used, copied,
 * modified, or distributed without the express written permission of
 * Via Transportation, Inc.
 */

import { selectUnit } from "@formatjs/intl-utils";
import isAfter from "date-fns/isAfter";
import isBefore from "date-fns/isBefore";
import isSameMinute from "date-fns/isSameMinute";
import { DateTime, Settings as LuxonSettings, Zone } from "luxon";
import { stringify } from "query-string";

import { isArray, isDate, isNull, isObject, mapKeys, mapValues, snakeCase } from "lodash";

import { ExtendedFetchBaseQueryError } from "common/types/ExtendedFetchBaseQueryError";
import {
    NotificationMessage,
    NotificationProps,
    NotificationType,
} from "common/types/Notification";

declare const AndroidInterface: any;

export const toCamelCase = (value: string) =>
    value.replace(/[_|-][a-z]/g, (m) => m[1].toUpperCase());

export const convertKeysToCamelCase = (obj: Record<string, any>) =>
    Object.keys(obj).reduce(
        (acc, val) => ({
            ...acc,
            [toCamelCase(val)]: obj[val],
        }),
        {}
    );

export const toSnakeCase = (value: string) =>
    value
        .replace(/-/g, "_")
        .replace(/[\w][A-Z]/g, (m) => m[0] + "_" + m[1])
        .toLowerCase();

export const convertKeysToSnakeCase = (obj: Record<string, any>) =>
    Object.keys(obj).reduce(
        (acc, val) => ({
            ...acc,
            [toSnakeCase(val)]: obj[val],
        }),
        {}
    );

export const convertObjectKeysToSnakeCase = (obj: any): any => {
    if (isArray(obj)) {
        return obj.map((item) => convertObjectKeysToSnakeCase(item)); // Recursively apply to array elements
    } else if (isObject(obj) && !isDate(obj) && !isNull(obj)) {
        return mapValues(
            mapKeys(obj, (_value, key) => snakeCase(key)), // Convert keys to snake case
            (value) => convertObjectKeysToSnakeCase(value) // Recursively apply to values
        );
    }
    return obj; // Return the value if it's not an object or array
};

export const buildUrl = (
    path: string,
    query: Record<string, boolean | number | string | string[]>
) => [path, stringify(convertKeysToSnakeCase(query))].join("?");

export const getRelativeTimeProps = (timestamp: string) =>
    selectUnit(Date.parse(timestamp), Date.now());

export const isISODate = (value: string) => {
    const re =
        /(\d{4}-[01]\d-[0-3]\dT[0-2]\d:[0-5]\d:[0-5]\d\.\d+([+-][0-2]\d:[0-5]\d|Z))|(\d{4}-[01]\d-[0-3]\dT[0-2]\d:[0-5]\d:[0-5]\d([+-][0-2]\d:[0-5]\d|Z))|(\d{4}-[01]\d-[0-3]\dT[0-2]\d:[0-5]\d([+-][0-2]\d:[0-5]\d|Z))/;
    return re.test(value);
};

export const mergePhoneParts = (phoneCode: string, phoneNumber: string) =>
    [
        phoneCode.replace(/\s/g, ""),
        parseInt(phoneNumber.replace(/\s|-|\(|\)/g, ""), 10).toString(),
    ].join("");

export const removeInvalidPhoneChars = (value: string) =>
    value
        .split("")
        .filter((char) => /[\d\s().+-]/.test(char))
        .join("");

export const replacer = (_: string, value: any) => {
    switch (typeof value) {
        case "object":
            if (Array.isArray(value) || value instanceof Date) {
                return value;
            } else if (value) {
                return convertKeysToSnakeCase(value);
            } else {
                return value;
            }
        default:
            return value;
    }
};

export const reviver = (_: string, value: any) => {
    switch (typeof value) {
        case "object":
            if (Array.isArray(value)) {
                return value;
            } else if (value) {
                return convertKeysToCamelCase(value);
            } else {
                return value;
            }
        default:
            return value;
    }
};

export const toLocalISOString = (date: Date) => {
    const offsetInMinutes = date.getTimezoneOffset();
    const offsetInHours =
        offsetInMinutes === 0
            ? "Z"
            : (offsetInMinutes > 0 ? "-" : "+") +
              (Math.abs(offsetInMinutes / 60) | 0).toString().padStart(2, "0") +
              ":" +
              Math.abs(offsetInMinutes % 60)
                  .toString()
                  .padStart(2, "0");
    return (
        date.getFullYear() +
        "-" +
        (date.getMonth() + 1).toString().padStart(2, "0") +
        "-" +
        date.getDate().toString().padStart(2, "0") +
        "T" +
        date.getHours().toString().padStart(2, "0") +
        ":" +
        date.getMinutes().toString().padStart(2, "0") +
        ":" +
        date.getSeconds().toString().padStart(2, "0") +
        "." +
        date.getMilliseconds().toString().padStart(3, "0") +
        offsetInHours
    );
};

export const toNextHalfHour = (minutes: number) => {
    const durationHours = Math.floor(minutes / 60);
    const durationMinutes = minutes % 60;
    return durationMinutes === 0
        ? durationHours
        : durationMinutes <= 30
        ? durationHours + 0.5
        : durationHours + 1;
};

export const isFullDay = (start: Date, end: Date) => {
    const timeZone = (LuxonSettings.defaultZone as Zone).name;
    const startDate = DateTime.fromJSDate(start).setZone(timeZone);
    const endDate = DateTime.fromJSDate(end).setZone(timeZone);
    const startOfDayDate = startDate.startOf("day").toJSDate();
    const endOfDayDate = endDate.endOf("day").toJSDate();

    return (
        isSameMinute(startOfDayDate, startDate.toJSDate()) &&
        isSameMinute(endOfDayDate, endDate.toJSDate())
    );
};

export const isFullDayOrMore = (start: Date, end: Date) => {
    const timeZone = (LuxonSettings.defaultZone as Zone).name;
    const startDate = DateTime.fromJSDate(start).setZone(timeZone);
    const endDate = DateTime.fromJSDate(end).setZone(timeZone);
    const startOfDayDate = startDate.startOf("day").toJSDate();
    const endOfDayDate = endDate.endOf("day").toJSDate();

    return (
        isFullDay(start, end) ||
        (isSameMinute(startOfDayDate, startDate.toJSDate()) &&
            isAfter(endOfDayDate, endDate.toJSDate()))
    );
};

export const isStartFullDay = (start: Date, end: Date) => {
    const timeZone = (LuxonSettings.defaultZone as Zone).name;
    const startDate = DateTime.fromJSDate(start).setZone(timeZone);
    const endDate = DateTime.fromJSDate(end).setZone(timeZone);
    const startOfDayDate = startDate.startOf("day").toJSDate();

    if (!isSameMinute(startOfDayDate, startDate.toJSDate())) return false;

    return (
        isSameMinute(startDate.endOf("day").toJSDate(), endDate.toJSDate()) ||
        isAfter(endDate.toJSDate(), startDate.endOf("day").toJSDate())
    );
};

export const isEndFullDay = (start: Date, end: Date) => {
    const timeZone = (LuxonSettings.defaultZone as Zone).name;
    const startDate = DateTime.fromJSDate(start).setZone(timeZone);
    const endDate = DateTime.fromJSDate(end).setZone(timeZone);
    const startOfDayDate = startDate.startOf("day").toJSDate();
    const endOfDayDate = endDate.endOf("day").toJSDate();

    if (!isSameMinute(endOfDayDate, endDate.toJSDate())) return false;
    return (
        isSameMinute(startOfDayDate, startDate.toJSDate()) ||
        isBefore(startDate.toJSDate(), endDate.startOf("day").toJSDate())
    );
};

export const isJwt = (token: string) => !!(token && token.split(".").length === 3);

export const parseJwt = (token: string) => {
    const base64Url = token.split(".")[1];

    if (!base64Url) return {};

    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
        atob(base64)
            .split("")
            .map((char) => "%" + ("00" + char.charCodeAt(0).toString(16)).slice(-2))
            .join("")
    );

    return JSON.parse(jsonPayload);
};

export const obtainDriverAppIDToken = (): Promise<string> =>
    new Promise((resolve, reject) => {
        (window as any).onTokenReceived = (idToken: string) => resolve(idToken);

        try {
            if (typeof (window as any).webkit !== "undefined" && (window as any).webkit !== null) {
                // iOS
                (window as any).webkit.messageHandlers.jsHandler.postMessage("askTokenAsync");
            } else if (typeof AndroidInterface !== "undefined" && AndroidInterface !== null) {
                // Android
                AndroidInterface.askTokenAsync();
            }
        } catch (error) {
            reject(error);
        }
    });

export const getErrorName = (
    error: ExtendedFetchBaseQueryError | Error | any
): NotificationMessage | string => {
    if (typeof error === "string" || error instanceof String) {
        return error as string;
    }
    if ((error as ExtendedFetchBaseQueryError)?.status) {
        if ((error as ExtendedFetchBaseQueryError)?.data) {
            if (((error as ExtendedFetchBaseQueryError)?.data as Error)?.message) {
                return ((error as ExtendedFetchBaseQueryError)?.data as Error)
                    ?.message as NotificationMessage;
            }
            if (((error as ExtendedFetchBaseQueryError)?.data as any)?.error) {
                return ((error as ExtendedFetchBaseQueryError)?.data as any)
                    ?.error as NotificationMessage;
            }
            if (((error as ExtendedFetchBaseQueryError)?.data as Error)?.name) {
                return ((error as ExtendedFetchBaseQueryError)?.data as Error)
                    ?.name as NotificationMessage;
            }
        }
        return (error as any).error as string;
    }
    return ((error as any)?.data as NotificationMessage) || (error as Error)?.name;
};

export const transformResponse = (response) => {
    return JSON.parse(JSON.stringify(response), reviver);
};

export const getAnalyticsInfo = (notification: NotificationProps) => {
    return notification.error && notification.type === NotificationType.ERROR
        ? {
              sectionUrl: window.location.pathname,
              error: (notification?.error as ExtendedFetchBaseQueryError)?.errorMeta,
              isError: true,
              //notificationId: notification.id,
              notificationMessage: notification.message,
              ...(String(
                  (notification?.error as ExtendedFetchBaseQueryError)?.errorMeta?.data
              )?.includes("traceId")
                  ? {
                        traceID: (notification?.error as ExtendedFetchBaseQueryError)?.errorMeta
                            ?.data,
                    }
                  : {}),
              ...((notification?.error as ExtendedFetchBaseQueryError)?.errorMeta?.requestUrl
                  ? {
                        requestUrl: (notification?.error as ExtendedFetchBaseQueryError)?.errorMeta
                            ?.requestUrl,
                    }
                  : {}),
              ...(notification?.analyticsContext ? notification.analyticsContext : {}),
              ...(notification?.originalArgs?.shifts?.[0]?.shiftId && {
                  shiftId: notification?.originalArgs?.shifts?.[0]?.shiftId,
              }),
          }
        : {
              sectionUrl: window.location.pathname,
              // notificationId: notification.id,
              notificationMessage: notification.message,
              ...(notification?.analyticsContext ? notification.analyticsContext : {}),
          };
};

export const isDev = () =>
    window.location.href.includes("dev") || window.location.href.includes("viacal:3000");
